import { inject } from '@angular/core';
import { AbstractControl, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import {
	AcceptedFileMaxSize,
	AcceptedFiles,
	AcceptedImages,
} from '@marmon/shared/types/common';
import { MaxInputLength } from '@marmon/shared/types/form';

const PasswordPropertyValidators = [
	'length',
	'special',
	'number',
	'lower',
	'upper',
] as const;
type PasswordPropertyValidator = (typeof PasswordPropertyValidators)[number];

type PasswordValidators<T = string> = {
	[key in PasswordPropertyValidator]: T;
};

const RegExpValidators: PasswordValidators<RegExp> = {
	length: new RegExp(/^.{8,20}$/),
	special: new RegExp('[!-\\/:-@[-`{-~]'),
	number: new RegExp('[0-9]'),
	lower: new RegExp('[a-z]'),
	upper: new RegExp('[A-Z]'),
};

type GConstructor = new (...args: any[]) => any;
export function applyValidators<T extends GConstructor>(Base: T) {
	return class extends Base {
		readonly maxLength = MaxInputLength;

		readonly acceptedFiles = AcceptedFiles.join(',');
		readonly acceptedImages = AcceptedImages.join(',');
		readonly acceptedFileMaxSize = AcceptedFileMaxSize;

		readonly transloco = inject(TranslocoService);

		getRequiredValidator = (
			field: AbstractControl,
			translation = 'form.error.required.generic',
		) => {
			if (typeof field.value === 'string') {
				// check if strings are trimmed
				return field.value.trim().length === 0
					? { required: this.transloco.translate(translation) }
					: null;
			}
			return Validators.required(field)
				? { required: this.transloco.translate(translation) }
				: null;
		};

		getEmailValidator = (
			field: AbstractControl,
			translation = 'form.error.email.generic',
		) =>
			Validators.email(field)
				? { email: this.transloco.translate(translation) }
				: null;

		getPasswordValidator = (field: AbstractControl) => {
			const length = RegExpValidators.length.test(field.value);
			const special = RegExpValidators.special.test(field.value);
			const lower = RegExpValidators.lower.test(field.value);
			const upper = RegExpValidators.upper.test(field.value);
			const number = RegExpValidators.number.test(field.value);

			const error: Partial<PasswordValidators> = {};
			if (!length)
				error['length'] = this.transloco.translate(
					'auth.password-validator.length',
				);

			if (!lower)
				error['lower'] = this.transloco.translate(
					'auth.password-validator.lower',
				);

			if (!upper)
				error['upper'] = this.transloco.translate(
					'auth.password-validator.upper',
				);

			if (!number)
				error['number'] = this.transloco.translate(
					'auth.password-validator.number',
				);

			if (!special)
				error['special'] = this.transloco.translate(
					'auth.password-validator.special',
				);

			return Object.keys(error).length ? error : null;
		};

		getMatchingValidator = (
			field: AbstractControl,
			match: AbstractControl,
			translation: string,
		) =>
			field.value !== match.value
				? { match: this.transloco.translate(translation) }
				: null;

		getMaxLengthValidator = (field: AbstractControl, maxLength: number) =>
			field.value.length > maxLength
				? {
						maxLength: this.transloco.translate(
							'form.error.length.max-#',
							{ length: maxLength },
						),
					}
				: null;

		getMinLengthValidator = (field: AbstractControl, minLength = 1) =>
			field.value.length < minLength
				? {
						minLength: this.transloco.translate(
							'form.error.length.min-#',
							{ length: minLength },
						),
					}
				: null;
	};
}
